import {
  CopyButton,
  Notice,
  StatCard,
  StatGrid,
} from "@/components/tool-kit/primitives";
import { WIDGET_H2, WIDGET_H3 } from "@/components/tool-kit/styles";
import type { STANDARD_SCORE_CONVERTER_STRINGS } from "@/components/tools/standard-score-converter";
import {
  Explanation,
  Formula,
  ResultTable,
  WorkingPanel,
} from "@/components/tools/stats-descriptive/ui";
import { BellCurve } from "@/components/tools/stats-descriptive-more/bell-curve";
import { t } from "@/lib/i18n/interpolate";
import {
  formatStat,
  type NumberFormatter,
} from "@/lib/tool-kit/stats-descriptive/format";
import {
  type Band,
  type Conversion,
  LINEAR_SCALES,
  type ScaleId,
  type StandardScores,
  stanineTable,
} from "@/lib/tool-kit/stats-descriptive-more/standard-scores";

type Strings = typeof STANDARD_SCORE_CONVERTER_STRINGS;

export interface ScoreView {
  strings: Strings;
  fmt: NumberFormatter;
  num: (value: number) => string;
}

function pct(view: ScoreView, value: number): string {
  return view.fmt(value, { maximumFractionDigits: 2 });
}

/** The all-scales table: scale, mean, SD, score. */
export function scoreRows(scores: StandardScores, view: ScoreView): string[][] {
  const { strings, fmt, num } = view;
  const linear = (
    id: (typeof LINEAR_SCALES)[number]["id"],
    name: string,
    value: number
  ) => {
    const scale = LINEAR_SCALES.find((each) => each.id === id);
    return [name, num(scale?.mean ?? 0), num(scale?.sd ?? 1), num(value)];
  };
  return [
    linear("z", strings.rowZ, scores.z),
    linear("t", strings.rowT, scores.t),
    linear("iq", strings.rowIq, scores.iq),
    linear("nce", strings.rowNce, scores.nce),
    linear("scaled", strings.rowScaled, scores.scaled),
    linear("ceeb", strings.rowCeeb, scores.ceeb),
    [strings.rowStanine, num(5), num(2), fmt(scores.stanine)],
    [strings.rowSten, num(5.5), num(2), fmt(scores.sten)],
    [
      strings.rowPercentile,
      "",
      "",
      t(strings.percentSuffix, { value: pct(view, scores.percentile) }),
    ],
  ];
}

function bandRange(band: Band, view: ScoreView): string {
  const { strings, num } = view;
  if (!Number.isFinite(band.lowZ)) {
    return t(strings.openLow, { to: num(band.highZ) });
  }
  if (!Number.isFinite(band.highZ)) {
    return t(strings.openHigh, { from: num(band.lowZ) });
  }
  return t(strings.rangeTemplate, {
    from: num(band.lowZ),
    to: num(band.highZ),
  });
}

export function stanineRows(view: ScoreView): string[][] {
  const { strings, fmt } = view;
  return stanineTable().map(({ stanine, band }) => [
    fmt(stanine),
    bandRange(band, view),
    t(strings.rangeTemplate, {
      from: pct(view, band.lowPercentile),
      to: pct(view, band.highPercentile),
    }),
    t(strings.percentSuffix, {
      value: pct(view, band.highPercentile - band.lowPercentile),
    }),
  ]);
}

export function ScoreCurve({
  conversion,
  view,
}: {
  conversion: Conversion;
  view: ScoreView;
}) {
  const { strings, fmt, num } = view;
  const { z, scores, band } = conversion;
  const zTicks = [-3, -2, -1, 0, 1, 2, 3];
  const shadeTo = band ? band.highZ : z;
  return (
    <div className="space-y-2">
      <h3 className={WIDGET_H3}>{strings.curveTitle}</h3>
      <BellCurve
        label={t(strings.curveLabel, {
          z: num(z),
          percentile: pct(view, band ? band.highPercentile : scores.percentile),
        })}
        marker={{ z, label: t(strings.curveMarker, { z: num(z) }) }}
        rows={[
          {
            label: strings.axisZ,
            ticks: zTicks.map((tick) => ({ z: tick, text: fmt(tick) })),
          },
          {
            label: strings.axisT,
            ticks: zTicks.map((tick) => ({
              z: tick,
              text: fmt(50 + 10 * tick),
            })),
          },
          {
            label: strings.axisIq,
            ticks: zTicks.map((tick) => ({
              z: tick,
              text: fmt(100 + 15 * tick),
            })),
          },
        ]}
        shades={[{ from: Number.NEGATIVE_INFINITY, to: shadeTo }]}
      />
    </div>
  );
}

interface WorkingInput {
  scale: ScaleId;
  value: number;
  mean: number | null;
  sd: number | null;
}

function zLine(
  conversion: Conversion,
  input: WorkingInput,
  view: ScoreView
): string {
  const { strings, num } = view;
  const z = num(conversion.z);
  const value = num(input.value);
  if (input.scale === "percentile") {
    return t(strings.workPercentile, { value, z });
  }
  if (input.scale === "stanine" || input.scale === "sten") {
    return t(strings.workBanded, {
      value,
      z,
      centre: input.scale === "stanine" ? "5" : "5.5",
    });
  }
  const scale = LINEAR_SCALES.find((each) => each.id === input.scale);
  const mean = scale ? scale.mean : (input.mean ?? 0);
  const sd = scale ? scale.sd : (input.sd ?? 1);
  return t(input.scale === "raw" ? strings.workRaw : strings.workLinear, {
    value,
    mean: num(mean),
    sd: num(sd),
    z,
  });
}

export function ScoreWorking({
  conversion,
  input,
  view,
}: {
  conversion: Conversion;
  input: WorkingInput;
  view: ScoreView;
}) {
  const { strings, fmt, num } = view;
  const { scores } = conversion;
  const z = num(conversion.z);
  return (
    <WorkingPanel title={strings.workingTitle}>
      <Formula>{zLine(conversion, input, view)}</Formula>
      <Formula>
        {t(strings.workPercentileOut, {
          z,
          value: pct(view, scores.percentile),
        })}
      </Formula>
      <Formula>{t(strings.workT, { z, value: num(scores.t) })}</Formula>
      <Formula>{t(strings.workIq, { z, value: num(scores.iq) })}</Formula>
      <Formula>{t(strings.workNce, { z, value: num(scores.nce) })}</Formula>
      <Formula>
        {t(strings.workScaled, { z, value: num(scores.scaled) })}
      </Formula>
      <Formula>{t(strings.workCeeb, { z, value: num(scores.ceeb) })}</Formula>
      <Formula>
        {t(strings.workStanine, {
          raw: num(2 * conversion.z + 5),
          value: fmt(scores.stanine),
        })}
      </Formula>
      <Formula>
        {t(strings.workSten, {
          raw: num(2 * conversion.z + 5.5),
          value: fmt(scores.sten),
        })}
      </Formula>
      <Explanation>{strings.workNormal}</Explanation>
    </WorkingPanel>
  );
}

/** The sentence explaining that a stanine or sten input stands for a band. */
export function bandNotice(
  conversion: Conversion,
  scale: ScaleId,
  view: ScoreView
): string {
  const { strings, fmt } = view;
  const band = conversion.band as Band;
  return t(strings.bandNotice, {
    scale: scale === "stanine" ? strings.bandStanine : strings.bandSten,
    value: fmt(
      Math.round(conversion.scores[scale === "stanine" ? "stanine" : "sten"])
    ),
    zRange: bandRange(band, view),
    pRange: t(strings.rangeTemplate, {
      from: pct(view, band.lowPercentile),
      to: pct(view, band.highPercentile),
    }),
    z: view.num(conversion.z),
  });
}

function Headline({
  scores,
  view,
}: {
  scores: StandardScores;
  view: ScoreView;
}) {
  const { strings, fmt, num } = view;
  const percentile = formatStat(fmt, scores.percentile, 2);
  return (
    <div aria-live="polite" className="space-y-4">
      <StatGrid>
        <StatCard emphasis label={strings.cardZ} value={num(scores.z)} />
        <StatCard
          emphasis
          label={strings.cardPercentile}
          value={t(strings.percentSuffix, { value: percentile })}
        />
        <StatCard label={strings.cardT} value={num(scores.t)} />
        <StatCard label={strings.cardIq} value={num(scores.iq)} />
        <StatCard label={strings.cardNce} value={num(scores.nce)} />
        <StatCard label={strings.cardStanine} value={fmt(scores.stanine)} />
      </StatGrid>
      <CopyButton
        copiedLabel={strings.copied}
        label={strings.copySummary}
        text={t(strings.summaryText, {
          z: num(scores.z),
          percentile,
          t: num(scores.t),
          iq: num(scores.iq),
          nce: num(scores.nce),
          scaled: num(scores.scaled),
          ceeb: num(scores.ceeb),
          stanine: fmt(scores.stanine),
          sten: fmt(scores.sten),
        })}
      />
    </div>
  );
}

/** Everything under the form: headline cards, the curve, both tables and the working. */
export function ScoreResults({
  conversion,
  input,
  view,
}: {
  conversion: Conversion;
  input: WorkingInput;
  view: ScoreView;
}) {
  const { strings } = view;
  return (
    <section className="space-y-6">
      <h2 className={WIDGET_H2}>{strings.results}</h2>
      <Headline scores={conversion.scores} view={view} />
      {conversion.band ? (
        <Notice>{bandNotice(conversion, input.scale, view)}</Notice>
      ) : null}
      <ScoreCurve conversion={conversion} view={view} />
      <div className="space-y-2">
        <h3 className={WIDGET_H3}>{strings.tableTitle}</h3>
        <ResultTable
          head={[
            strings.colScale,
            strings.colMean,
            strings.colSd,
            strings.colScore,
          ]}
          rows={scoreRows(conversion.scores, view)}
          wrapFirstColumn
        />
      </div>
      <div className="space-y-2">
        <h3 className={WIDGET_H3}>{strings.stanineTitle}</h3>
        <ResultTable
          head={[
            strings.colStanine,
            strings.colZRange,
            strings.colPercentileRange,
            strings.colShare,
          ]}
          rows={stanineRows(view)}
        />
      </div>
      <ScoreWorking conversion={conversion} input={input} view={view} />
    </section>
  );
}
